//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { UnisonColor, UnisonIconType, theme } from '@bvengineering/unison-core';
import React from 'react';
import styled, { css } from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { MediumTooltip } from '@components/molecules/tooltip/Tooltip';
import { ellipsize } from '@utils/stringUtils';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface Props
  extends React.HTMLAttributes<HTMLDivElement>,
    ITrackableComponent {
  /**
   * Some usages of the `Label` (e.g. inside HorizontalScrolling molecules)
   * require the text overflow tooltip to be attached to the body element rather
   * than the label itself. This flag enables that behavior.
   */
  decorativeIcon?: UnisonIconType;
  inline?: boolean;
  isDisabled?: boolean;
  label: string;
  maxLength?: number;
  maxTooltipWidth?: string;
  onRemove?: () => void;
  shouldAppendTooltipToBody?: boolean;
  type?: Type;
}

type Type = 'default' | 'success' | 'error';

/**
 * The Label atom renders a string of text in a pill shaped container
 * with an optional remove button on the right. If the given string is
 * longer than the max length, the label will truncate with an ellipsis
 * in the chip and show the entire string in a tooltip on hover.
 *
 * The inline version of the label should only be used when placed inside
 * an input for a typeahead, or at design's direction.
 */
export const Label = ({
  className = '',
  decorativeIcon,
  inline = false,
  isDisabled = false,
  label,
  maxLength = 20,
  maxTooltipWidth,
  onRemove,
  shouldAppendTooltipToBody = false,
  trackingId,
  type = 'default',
}: Props) => {
  const ellipsizedLabel = ellipsize(label, {
    maxLength,
    noSpaceBeforeEllipsis: true,
  });

  const renderLabel = () => (
    <LabelWrapper className={className} inline={inline} type={type}>
      {decorativeIcon && (
        <DecorativeIcon inline={inline} name={decorativeIcon} />
      )}
      {ellipsizedLabel}
      {onRemove && (
        <RemoveButton
          buttonType={type}
          inline={inline}
          isDisabled={isDisabled}
          onClick={onRemove}
          trackingId={trackingId}
        />
      )}
    </LabelWrapper>
  );

  return (
    <MediumTooltip
      appendTarget={shouldAppendTooltipToBody ? 'body' : undefined}
      content={
        <TooltipContent maxTooltipWidth={maxTooltipWidth}>
          {label}
        </TooltipContent>
      }
      isDisabled={label.length <= maxLength}
      target={renderLabel()}
    />
  );
};

interface ILabelProps {
  inline: boolean;
  type: Type;
}

interface ILabelButtonProps {
  inline: boolean;
  buttonType: Type;
}

interface ILabelColors {
  background: UnisonColor;
  text: UnisonColor;
  removeIcon: UnisonColor;
}

const lableBackgroundColors: Record<Type, ILabelColors> = {
  default: {
    background: theme.colors.gray1,
    text: theme.colors.aliases.bodyPrimary,
    removeIcon: theme.colors.aliases.bodySecondary,
  },
  success: {
    background: theme.colors.green0,
    text: theme.colors.aliases.success,
    removeIcon: theme.colors.aliases.success,
  },
  error: {
    background: theme.colors.red0,
    text: theme.colors.aliases.error,
    removeIcon: theme.colors.aliases.error,
  },
};

const LabelWrapper = withTracking(
  styled.div<ILabelProps>(
    ({ inline, theme, type }) => `
    display: flex;
    vertical-align: middle;
    align-items: center;
    background-color: ${lableBackgroundColors[type].background};
    color: ${lableBackgroundColors[type].text};
    border-radius: ${theme.borderRadii.pill};
    font-size: ${inline ? theme.fontSizes[6] : theme.fontSizes[5]};
    padding: ${theme.spacings[1]} ${inline ? `calc(${theme.spacings[4]}/2)` : theme.spacings[3]};
  `,
  ),
);

const RemoveButton = styled(InvisibleButton).attrs<ILabelButtonProps>(
  ({ buttonType }) => ({
    iconName: 'close',
    iconColor: lableBackgroundColors[buttonType].removeIcon,
  }),
)<ILabelButtonProps>(
  ({ inline, theme }) => `
  && {
    margin-left: ${inline ? theme.spacings[1] : theme.spacings[2]};
    height: auto;
    min-width: auto;
  }
  svg {
    width: ${inline ? `calc(${theme.spacings[4]}/2)` : theme.spacings[3]};
    height: ${inline ? `calc(${theme.spacings[4]}/2)` : theme.spacings[3]};
  }
`,
);

/**
 * This is a wrapped label that adds a left margin of 8px to any label but the first in a given div. Because
 * this style is a formal design consideration, we're exporting it for the sake of consistency and usability.
 */
export const LabelWithMargin = (props: Props) => (
  <LabelWithMarginWrapper>
    <Label {...props} />
  </LabelWithMarginWrapper>
);

const LabelWithMarginWrapper = styled.div(
  ({ theme }) => `
  &:not(:first-of-type) {
    margin-left: ${theme.spacings[2]};
  }
`,
);

const TooltipContent = styled.div<{ maxTooltipWidth?: string }>(
  ({ maxTooltipWidth, theme }) => css`
    max-width: ${maxTooltipWidth ?? theme.mediumMeasure};
    overflow-wrap: break-word;
    word-break: break-all;
    white-space: normal;
  `,
);

const DecorativeIcon = styled(Icon)<Omit<ILabelProps, 'type'>>(
  ({ inline, theme }) => `
    margin-right: ${theme.spacings[2]};
    width: ${inline ? `calc(${theme.spacings[4]}/2)` : theme.spacings[3]};
    height: ${inline ? `calc(${theme.spacings[4]}/2)` : theme.spacings[3]};
`,
);

export default Label;
